import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image,
  Box,
  HStack,
  Button,
  Text,
  VStack,
  Link,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { ExternalLink, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  photoUrl: string;
  pageUrl: string;
  albumTitle?: string;
}

const PhotoModal: React.FC<PhotoModalProps> = ({
  isOpen,
  onClose,
  photoUrl,
  pageUrl,
  albumTitle,
}) => {
  const { t } = useTranslation();
  const [imageDimensions, setImageDimensions] = React.useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = React.useState(false);
  
  // Color mode values
  const controlsBg = useColorModeValue('white', 'gray.800');
  const controlsBorder = useColorModeValue('gray.200', 'gray.600');
  const controlsText = useColorModeValue('gray.700', 'gray.200');

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
    setImageLoaded(true);
  };

  // Calculate if close button should be inside or outside based on image aspect ratio
  const shouldShowCloseOutside = imageDimensions.width > imageDimensions.height * 1.5;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="full" 
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay 
        bg="blackAlpha.900" 
        backdropFilter="blur(8px)"
      />
      
      {!shouldShowCloseOutside && (
        <ModalCloseButton
          color="white"
          bg="blackAlpha.600"
          _hover={{ bg: 'blackAlpha.800' }}
          size="lg"
          top={4}
          right={4}
          zIndex={10}
          borderRadius="full"
        />
      )}
      
      <ModalContent 
        bg="transparent" 
        boxShadow="none" 
        maxW="100vw" 
        maxH="100vh"
        position="relative"
        overflow="hidden"
      >
        <ModalBody p={0} m={0}>
          <VStack spacing={0} align="stretch" h="100vh">
            {/* Image Container */}
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              minH="60vh"
            >
              <Image
                src={photoUrl}
                alt={albumTitle || t('gallery.photo_modal_title')}
                maxH="85vh"
                maxW="95vw"
                objectFit="contain"
                loading="eager"
                onLoad={handleImageLoad}
                fallback={
                  <Box 
                    w="400px" 
                    h="300px" 
                    bg="gray.200" 
                    borderRadius="lg"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color="gray.500">{t('common.loading')}</Text>
                  </Box>
                }
              />
              
              {/* Close button for wide images */}
              {shouldShowCloseOutside && imageLoaded && (
                <Button
                  position="absolute"
                  top={4}
                  right={4}
                  color="white"
                  bg="blackAlpha.600"
                  _hover={{ bg: 'blackAlpha.800' }}
                  borderRadius="full"
                  p={2}
                  minW="auto"
                  h="auto"
                  onClick={onClose}
                  zIndex={10}
                >
                  <Icon as={X} boxSize={6} />
                </Button>
              )}
            </Box>

            {/* Controls */}
            <Box
              bg={controlsBg}
              borderTop="1px solid"
              borderColor={controlsBorder}
              p={4}
              boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1)"
            >
              <VStack spacing={3} align="stretch" maxW="800px" mx="auto">
                {albumTitle && (
                  <Text 
                    fontSize="md" 
                    fontWeight="600" 
                    color={controlsText}
                    textAlign="center"
                    noOfLines={2}
                  >
                    {albumTitle}
                  </Text>
                )}
                
                <HStack spacing={3} justify="center" flexWrap="wrap">
                  <Button
                    as="a"
                    href={pageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    leftIcon={<ExternalLink size={18} />}
                    colorScheme="purple"
                    size="sm"
                    variant="solid"
                  >
                    {t('gallery.view_original')}
                  </Button>
                  
                  <Button
                    onClick={onClose}
                    colorScheme="gray"
                    size="sm"
                    variant="outline"
                  >
                    {t('common.close')}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PhotoModal;
